// The coin catalogue with its artwork attached.
//
// The rows themselves live in ./coinRows.ts, with no icons in them. That split
// is not tidiness: the icons are real images now, and a test runner cannot
// import a .webp. With the two in one file the catalogue test could not load
// the catalogue at all, so the one guard that checks our coin list against the
// server's token table silently stopped checking anything.
//
// So: data there, pictures here, and everything a screen used to import from
// this file still comes from this file.

import { BnbIcon } from '../components/icons/BnbIcon';
import { DogeIcon } from '../components/icons/DogeIcon';
import { XrpIcon } from '../components/icons/XrpIcon';
import { BtcIcon } from '../components/icons/BtcIcon';
import { EthIcon } from '../components/icons/EthIcon';
import { HypeIcon } from '../components/icons/HypeIcon';
import { SolIcon } from '../components/icons/SolIcon';
import { TonIcon } from '../components/icons/TonIcon';
import { TrxIcon } from '../components/icons/TrxIcon';
import { UsdcIcon } from '../components/icons/UsdcIcon';
import { UsdtIcon } from '../components/icons/UsdtIcon';
import { XmrIcon } from '../components/icons/XmrIcon';
import { ZecIcon } from '../components/icons/ZecIcon';
import {
  COIN_ROWS,
  DEFAULT_COIN_ROW,
  SENDABLE_COIN_ROWS,
  SWAPPABLE_COIN_ROWS,
  UNIQUE_COIN_ROWS,
  coinRowsForChain,
  type CoinRow,
} from './coinRows';

export type { CoinSymbol } from './coinRows';
export { coinKey } from './coinRows';

/** A catalogue row plus the icon the UI draws for it. */
export type Coin = CoinRow & { Icon: typeof UsdtIcon };

/**
 * Icon per token × chain.
 *
 * Keyed by the pair rather than by the symbol alone because the same
 * stablecoin appears on several chains and they share one mark, while each
 * chain's own coin has its own. A row with no entry here would render with no
 * icon, so `withIcon` refuses instead.
 */
const ICONS: Record<string, typeof UsdtIcon> = {
  'USDT-trx': UsdtIcon,
  'USDT-eth': UsdtIcon,
  'USDT-bsc': UsdtIcon,
  'USDC-eth': UsdcIcon,
  'USDC-trx': UsdcIcon,
  'USDC-bsc': UsdcIcon,
  'BTC-btc': BtcIcon,
  'ETH-eth': EthIcon,
  'TRX-trx': TrxIcon,
  'USDT-sol': UsdtIcon,
  'USDC-sol': UsdcIcon,
  'SOL-sol': SolIcon,
  'USDT-hyperevm': UsdtIcon,
  'USDC-hyperevm': UsdcIcon,
  'HYPE-hyperevm': HypeIcon,
  'USDT-ton': UsdtIcon,
  'TON-ton': TonIcon,
  'XMR-xmr': XmrIcon,
  'ZEC-zec': ZecIcon,
  'BNB-bsc': BnbIcon,
  'DOGE-doge': DogeIcon,
  'XRP-xrp': XrpIcon,
};

function withIcon(row: CoinRow): Coin {
  const Icon = ICONS[`${row.symbol}-${row.chain}`];
  if (!Icon) throw new Error(`no icon for ${row.symbol} on ${row.chain}`);
  return { ...row, Icon };
}

export const COINS: Coin[] = COIN_ROWS.map(withIcon);
export const SWAPPABLE_COINS: Coin[] = SWAPPABLE_COIN_ROWS.map(withIcon);
export const SENDABLE_COINS: Coin[] = SENDABLE_COIN_ROWS.map(withIcon);
export const UNIQUE_COINS: Coin[] = UNIQUE_COIN_ROWS.map(withIcon);
export const DEFAULT_COIN: Coin = withIcon(DEFAULT_COIN_ROW);
export const coinsForChain = (chain?: Coin['chain']): Coin[] =>
  coinRowsForChain(chain).map(withIcon);
